// Window briefing — a deterministic natural-language summary of a chosen time
// window. Every number it states is computed from the forecast; nothing is
// invented, so it is honest, auditable and offline-capable. Its structured
// facts are also what an optional AI-polish layer would rephrase later.
//
// Ported from the v1 renderAnalysis. Unlike v1 it is PURE: it reads no DOM and
// no sliders — every input arrives in BriefingInput — and returns an HTML
// string. Style hooks reuse the v1 class names (.lede, .verdict, .b-note) and
// emit inline `background:var(--good)` etc.; v1's --serious maps to --warn.

import type { PointForecast } from "../forecast/openmeteo.js";
import { firstWithData } from "../forecast/openmeteo.js";
import type { Solar } from "../forecast/solar.js";
import type { Vessel } from "./registry.js";
import { timeOfLabel } from "../time/cursor.js";
import { fmtDayAmPm } from "../ui/time.js";
import { convLen, fmtDepth, fmtKm, fmtLen, getUnitSystem, lenUnit } from "../ui/units.js";

export interface BriefingInput {
  fc: PointForecast;
  solar: Solar | null;
  vessel: Vessel | null;
  startHours: number;
  lengthHours: number;
}

// --- Reference tables (module-private, ported verbatim from v1) --------------

/** Beaufort: [maxKn inclusive, term, sea/deck feel]. NOAA/WMO knot boundaries. */
const BEAUFORT: readonly [number, string, string][] = [
  [1, "calm", "glassy, mirror-like"],
  [3, "light air", "ripples, barely felt"],
  [6, "light breeze", "small wavelets, wind felt on face"],
  [10, "gentle breeze", "large wavelets, flags stir"],
  [16, "moderate breeze", "small waves, some whitecaps, dust lifts"],
  [21, "fresh breeze", "moderate waves, many whitecaps, spray begins"],
  [27, "strong breeze", "large waves, extensive whitecaps, spray"],
  [33, "near gale", "sea heaps up, foam blows in streaks"],
  [40, "gale", "moderately high waves, edges break into spindrift"],
  [47, "strong gale", "high waves, dense foam, rolling seas, reduced visibility"],
  [55, "storm", "very high waves, sea white with foam, heavy tumbling"],
  [63, "violent storm", "exceptionally high waves"],
  [999, "hurricane force", "air filled with foam and spray, sea completely white"],
];

const beaufort = (kn: number): [number, string, string] =>
  BEAUFORT.find((b) => kn <= b[0]) ?? BEAUFORT[BEAUFORT.length - 1];

/** WMO sea-state / Douglas: significant wave height (m) → term. */
const SEASTATE: readonly [number, string][] = [
  [0.1, "calm (glassy)"],
  [0.5, "calm to smooth"],
  [1.25, "slight"],
  [2.5, "moderate"],
  [4, "rough"],
  [6, "very rough"],
  [9, "high"],
  [14, "very high"],
  [999, "phenomenal"],
];

const seaTerm = (m: number): string =>
  (SEASTATE.find((s) => m <= s[0]) ?? SEASTATE[SEASTATE.length - 1])[1];

// --- Seakeeping --------------------------------------------------------------
//
// Standard linear wave theory and textbook seakeeping. Nothing here is modelled
// or fitted; each relation is the ordinary one:
//
//   wavelength   ω² = g·k·tanh(k·d)          — linear dispersion; in deep water
//                                              (d > L/2) this is L = gT²/2π ≈ 1.56T²
//   steepness    S = H / L                   — waves break near S ≈ 1/7
//   pitch        worst when L ≈ LOA          — a hull pitches hardest in waves
//                                              about its own length
//   roll         worst when T ≈ natural roll — resonance
//   slamming     needs the bow to emerge     — so it needs height against draft,
//                                              not merely a short period
//
// This replaces a single T<6s test that returned "short and steep — sharp,
// slamming motion" for ANY short period, with no reference to wave height or to
// the ship. It told the master of a 48.5 m vessel that 0.7 m seas would slam
// her. Period alone cannot say that; steepness and height against draft can.
//
// The band EDGES below are seamanship rules of thumb, not measurements, and the
// briefing's closing line already declares comfort thresholds provisional. The
// quantities they are drawn from are printed alongside the words so the reader
// can disagree with the adjective and still have the number.

const G = 9.81;

/**
 * Wavelength in metres for a wave period at a given depth, from the linear
 * dispersion relation solved by fixed-point iteration. A null or non-positive
 * depth is treated as deep water, which is also what the iteration converges to
 * once d exceeds about half a wavelength.
 */
export function wavelengthM(periodS: number, depthM: number | null): number | null {
  if (!Number.isFinite(periodS) || periodS <= 0) return null;
  const deep = (G * periodS * periodS) / (2 * Math.PI);
  if (depthM == null || !Number.isFinite(depthM) || depthM <= 0) return deep;
  let L = deep;
  for (let i = 0; i < 80; i++) {
    const next = deep * Math.tanh((2 * Math.PI * depthM) / L);
    if (Math.abs(next - L) < 1e-4) return next;
    L = next;
  }
  return L;
}

/** Steepness spoken the way a mariner says it: 1 in N. */
function steepnessAsRatio(s: number): string {
  return `1:${Math.round(1 / s)}`;
}

/**
 * How the sea feels, graded by steepness rather than by period alone.
 *
 * Deliberately says nothing about swell or chop. Steepness measures how sharp a
 * sea is, not what kind it is: a 0.75 m sea at 4.5 s comes out at 1:42, which is
 * genuinely not steep, but calling it "an easy swell" would be wrong — 4.5 s is
 * wind sea. Whether it is swell or chop is the Sea character line's job, from the
 * partition; this one keeps to motion.
 */
function steepnessFeel(s: number): string {
  // Every phrase is RELATIVE — "for their height" — because that is what
  // steepness is, and saying it plainly is what stops the number being misread as
  // severity. A first cut used absolute words and called 8 m Southern Ocean seas
  // "a working chop": true about the shape, absurd about the sea, and precisely
  // the sort of line that loses a master's trust. Height is stated in the same
  // sentence and the verdict is on the panel; this clause describes only shape.
  //
  // No "steepness" in the strings either — the ratio is already printed, and
  // repeating it gave "steepness 1:46 — low steepness — easy motion".
  if (s < 1 / 60) return "very well spaced for their height — a long, rounded heave";
  if (s < 1 / 40) return "well spaced for their height — rounded, easy motion";
  if (s < 1 / 25) return "moderately spaced — a regular working motion";
  if (s < 1 / 15) return "steep for their height — sharp, jarring motion";
  return "very steep — crests breaking, punishing motion";
}

/**
 * How the hull meets it, from wavelength against her length.
 *
 * Pitch peaks when the wavelength is near the ship's length, so the peak band is
 * kept narrow around 1.0 and the approaches are graded rather than lumped in with
 * it. A first cut called anything from 0.7 to 1.6 "her worst band for pitching",
 * which claimed the peak for a 0.72 that is only on the way to it.
 */
function pitchFeel(r: number): string {
  if (r < 0.6) return "waves much shorter than the hull — she bridges them, little pitch";
  if (r < 0.8) return "waves shorter than the hull — modest pitching";
  if (r <= 1.3) return "wavelength near her own length — her worst band for pitching";
  if (r <= 2) return "waves longer than the hull — she begins to contour them";
  return "waves much longer than the hull — she contours them, an easy heave";
}

/** Smallest angle between two compass bearings, 0–180°. */
export function angleBetween(a: number, b: number): number {
  const d = Math.abs(((a - b) % 360) + 360) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Circular mean of a bearing series over [a,b] — a plain arithmetic mean is wrong
 * for angles (350° and 10° average to 180°, the opposite direction).
 */
export function meanBearing(arr: Series, a: number, b: number): number | null {
  if (!arr) return null;
  let x = 0;
  let y = 0;
  let n = 0;
  for (let i = Math.max(0, a); i <= b && i < arr.length; i++) {
    const v = arr[i];
    if (v == null || !Number.isFinite(v)) continue;
    const r = (v * Math.PI) / 180;
    x += Math.cos(r);
    y += Math.sin(r);
    n++;
  }
  if (n === 0 || (x === 0 && y === 0)) return null;
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

// --- Derived sea conditions, as reusable detectors ---------------------------
//
// The briefing states these as prose about a WINDOW. The conditions table marks
// them per COLUMN. Same conditions, two surfaces — so the thresholds live here,
// exported, and both read them. A number in this block is changed once.
//
// Everything below is a predicate over quantities the forecast already carries;
// none of it is new science. The prose in buildBriefing calls these too, so the
// paragraph and the marked column cannot disagree about whether the sea is
// crossed at 3 p.m. on Thursday.

/** How near the wave period has to run to a hull's natural roll period before
 *  resonance is worth naming. Ratio offset from 1.0. */
export const ROLL_RESONANCE = { hard: 0.12, lively: 0.3 } as const;

/** Roll resonance for one period against one hull, or null when it is not
 *  close enough to say anything. The single sharpest thing that can be said
 *  about how a hull will behave in a given sea, and the reason this detector
 *  exists outside the prose. */
export function rollResonance(
  periodS: number | null,
  rollPeriodS: number | null,
): "hard" | "lively" | null {
  if (periodS == null || rollPeriodS == null) return null;
  if (!Number.isFinite(periodS) || !Number.isFinite(rollPeriodS) || rollPeriodS <= 0) return null;
  const off = Math.abs(periodS / rollPeriodS - 1);
  if (off <= ROLL_RESONANCE.hard) return "hard";
  if (off <= ROLL_RESONANCE.lively) return "lively";
  return null;
}

/** Both trains have to be real for the sea to feel crossed — see the reasoning
 *  in buildBriefing's cross-sea note. */
export const CROSS_SEA = { sepDeg: 60, confusedDeg: 90, minLesserM: 0.3, minRatio: 0.33 } as const;

/** The separation in degrees when swell and wind sea are genuinely crossed, or
 *  null. Callers read ≥ CROSS_SEA.confusedDeg as "confused" rather than merely
 *  "uneven", exactly as the prose does. */
export function crossSea(
  swellDir: number | null,
  windSeaDir: number | null,
  swellH: number | null,
  windSeaH: number | null,
): number | null {
  if (swellDir == null || windSeaDir == null || swellH == null || windSeaH == null) return null;
  if (!Number.isFinite(swellH) || !Number.isFinite(windSeaH)) return null;
  const sep = angleBetween(swellDir, windSeaDir);
  const lesser = Math.min(swellH, windSeaH);
  const greater = Math.max(swellH, windSeaH);
  if (sep < CROSS_SEA.sepDeg) return null;
  if (lesser < CROSS_SEA.minLesserM) return null;
  if (greater <= 0 || lesser / greater < CROSS_SEA.minRatio) return null;
  return sep;
}

/** Slamming needs a STEEP sea and enough height against the draft for the bow
 *  to come out. Period alone never says this — the reason the old T < 6 s test
 *  was removed (see the seakeeping note above). */
export const SLAM = { steep: 1 / 25, draftRatioWarn: 0.6, draftRatioHard: 1 } as const;

export function slamRisk(
  waveM: number | null,
  periodS: number | null,
  depthM: number | null,
  draftM: number | null,
): "hard" | "warn" | null {
  if (waveM == null || periodS == null || draftM == null || draftM <= 0) return null;
  const L = wavelengthM(periodS, depthM);
  if (L == null || L <= 0) return null;
  if (waveM / L < SLAM.steep) return null;
  const vsDraft = waveM / draftM;
  if (vsDraft >= SLAM.draftRatioHard) return "hard";
  if (vsDraft >= SLAM.draftRatioWarn) return "warn";
  return null;
}

/** Swell period at or above which the briefing calls it distant-swell energy —
 *  "a long, predictable, rolling motion". */
export const LONG_SWELL_S = 9;

export function longSwell(swellPeriodS: number | null): boolean {
  return swellPeriodS != null && Number.isFinite(swellPeriodS) && swellPeriodS >= LONG_SWELL_S;
}

/** Wind blowing against the swell steepens crests beyond what height and period
 *  imply, and is why a forecast can under-read. */
export const OPPOSED_WIND = { sepDeg: 120, minWindKn: 15 } as const;

export function windAgainstSwell(
  windDir: number | null,
  swellDir: number | null,
  windKn: number | null,
): boolean {
  if (windDir == null || swellDir == null || windKn == null) return false;
  if (windKn < OPPOSED_WIND.minWindKn) return false;
  return angleBetween(windDir, swellDir) >= OPPOSED_WIND.sepDeg;
}

// --- Forecast confidence -----------------------------------------------------

/**
 * The GEFS spread bands, in knots of p95−p05 width. The briefing says "tight —
 * a confident forecast" below `tight` and "wide — the atmosphere hasn't
 * settled" at or above `wide`; the conditions table's per-day predictability
 * badge is a graded read of the same two numbers. Two surfaces, one pair of
 * thresholds — a badge saying 90% over a paragraph saying the atmosphere has
 * not settled is exactly the contradiction this export exists to prevent.
 */
export const ENSEMBLE_WIDTH_KN = { tight: 8, wide: 15 } as const;

/** The ensemble p95−p05 wind width at one hour, in knots, or null when that
 *  hour has no members. */
export function ensembleWidthAt(fc: PointForecast, i: number): number | null {
  const mem = fc.ensembleMembers?.[i];
  if (!mem || mem.length === 0) return null;
  const s = [...mem].sort((x, y) => x - y);
  return quantile(s, 0.95) - quantile(s, 0.05);
}

/**
 * One observation about how the sea will feel on this hull, with a 0–1 salience
 * so the briefing can lead with whatever actually dominates today.
 *
 * Ranking is the point. A fixed running order produces the same paragraph every
 * day, and a briefing that reads identically on a flat Tuesday and a bad Friday
 * gets skimmed and then ignored — which is worse than not having it. These are
 * scored and the top two are printed, so the sentence changes when the sea does.
 */
interface FeelNote {
  score: number;
  text: string;
}

// --- Stats helpers ----------------------------------------------------------

interface Stat {
  min: number;
  max: number;
  mean: number;
}

export type Series = (number | null)[] | null | undefined;

const stats = (arr: Series, a: number, b: number): Stat | null => {
  const v: number[] = [];
  for (let i = a; i <= b; i++) {
    const x = arr?.[i];
    if (x !== null && x !== undefined) v.push(x);
  }
  if (!v.length) return null;
  return {
    min: Math.min(...v),
    max: Math.max(...v),
    mean: v.reduce((s, x) => s + x, 0) / v.length,
  };
};

/** Pooled stats across several model arrays over [a, b]. */
const poolStats = (arrs: Series[], a: number, b: number): Stat | null => {
  const v: number[] = [];
  for (let i = a; i <= b; i++)
    for (const arr of arrs) {
      const x = arr?.[i];
      if (x !== null && x !== undefined) v.push(x);
    }
  if (!v.length) return null;
  return {
    min: Math.min(...v),
    max: Math.max(...v),
    mean: v.reduce((s, x) => s + x, 0) / v.length,
  };
};

/** Linear-interpolation quantile of an ascending-sorted array. */
function quantile(sorted: number[], q: number): number {
  const p = (sorted.length - 1) * q;
  const lo = Math.floor(p);
  const hi = Math.min(lo + 1, sorted.length - 1);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (p - lo);
}

const rng = (s: Stat, u: string, d = 0): string =>
  s.min.toFixed(d) === s.max.toFixed(d)
    ? `${s.min.toFixed(d)} ${u}`
    : `${s.min.toFixed(d)}–${s.max.toFixed(d)} ${u}`;

/** A wave-height range in the display unit — the metric adjectives beside it
 *  (seaTerm, verdicts) keep reading the metric stat, so only the printed number
 *  moves with the Units preference. Feet read in whole numbers: "4–6 ft". */
const rngLen = (s: Stat): string => {
  const imp = getUnitSystem() === "imperial";
  return rng({ min: convLen(s.min), max: convLen(s.max), mean: s.mean }, lenUnit(), imp ? 0 : 1);
};

// --- Briefing ---------------------------------------------------------------

export function buildBriefing(input: BriefingInput): string {
  const { fc, solar, vessel } = input;
  const times = fc.times;
  if (!times.length) return "";

  const nowIdx = fc.nowIndex;
  const start = input.startHours;
  const len = input.lengthHours;
  const a = Math.min(times.length - 1, Math.max(0, nowIdx) + start);
  const b = Math.min(times.length - 1, a + len - 1);
  // Through the shared clock (ui/time.ts): the prose names the same hours the
  // axis ticks and window chips do, in whichever clock the console is set to.
  const fmt = (i: number): string => {
    const t = timeOfLabel(times[i] ?? "");
    return Number.isFinite(t) ? fmtDayAmPm(t) : times[i] ?? "";
  };

  const vlabel = (vessel?.name ?? "").replace(/ —.*/, "");
  const rollT = vessel?.roll_period_s ?? null;

  // Model arrays mapped from PointForecast (v1's per-model pools).
  const windByModel: Series[] = fc.wind ? fc.wind.models.map((m) => m.data) : [];
  const waveByModel: Series[] = fc.waves ? fc.waves.height.map((m) => m.data) : [];
  // PERIOD IS NOT POOLED, and this is the one place in the file where a model
  // pool would have been the obvious thing to write.
  //
  // Height is pooled across all four models on purpose: they publish the same
  // quantity, so the width of the pool is real disagreement worth reporting.
  // Period is not the same quantity across the four — GFS-Waves publishes peak
  // period, the other three energy period (see WAVE_MODELS) — so pooling them
  // produced a mean that was not any physical period at all, sitting between
  // two definitions, and a min–max range whose width was mostly the definition
  // gap. That mean then drove roll resonance, slamming, wavelength and
  // steepness: a fabricated number gating a go/no-go call, which is the exact
  // thing CONVENTIONS.md "Errors & honesty" rules out.
  //
  // So: ONE model, the same first-with-data pick the conditions table makes, so
  // the prose and the table's R/s marks are computed from the same series and
  // cannot disagree about the same hour — which help/content.ts already
  // promised they could not. The sentence names which model and which quantity.
  const periodModel = fc.waves ? firstWithData(fc.waves.period) : null;
  const periodSeries: Series = periodModel?.data;
  const swellH: Series = fc.partition?.swellH;
  const windSeaH: Series = fc.partition?.windSeaH;
  const swellPer: Series = fc.partition?.swellPer;
  const curVelKn: Series = fc.current?.speedKn;

  // Geographic scope — a nearshore, shallow-water boat is out of its envelope
  // over deep water, whatever the weather. depthM is positive metres of water.
  const depthM = fc.depthM;
  let scope: { lvl: number; msg: string } | null = null;
  if (vessel?.nearshore && depthM != null) {
    scope =
      depthM > 200
        ? {
            lvl: 2,
            msg:
              `<strong>${vlabel || "This boat"} is a nearshore, shallow-water, daylight-only survey boat.</strong> ` +
              `This point sits over roughly ${fmtDepth(depthM)} of water — open, deep ocean, well ` +
              `outside her operating envelope. She does not work here; the conditions below are shown for reference only.`,
          }
        : depthM > 40
          ? {
              lvl: 1,
              msg:
                `Water here is about ${fmtDepth(depthM)} — on the shelf but deeper than the ` +
                `${vlabel || "Barnacle"}'s usual shallow, nearshore ground; workable only near her inshore limits.`,
            }
          : {
              lvl: 0,
              msg:
                `Shallow nearshore water (~${fmtDepth(depthM)}) — within the ` +
                `${vlabel || "Barnacle"}'s shallow-water survey envelope.`,
            };
  }

  // Wind range across the window, plus mean per-hour ensemble width (p95−p05)
  // as a true forecast-confidence measure isolated from hour-to-hour variation.
  const windPool: number[] = [];
  let ensWidthSum = 0;
  let ensWidthN = 0;
  for (let i = a; i <= b; i++) {
    const mem = fc.ensembleMembers?.[i];
    if (mem && mem.length) {
      for (const v of mem) windPool.push(v);
      const w = ensembleWidthAt(fc, i);
      if (w !== null) {
        ensWidthSum += w;
        ensWidthN++;
      }
    }
  }
  const ensWidth = ensWidthN ? ensWidthSum / ensWidthN : null;
  const windModelPool = poolStats(windByModel, a, b);
  const wind: Stat | null = windPool.length
    ? {
        min: Math.min(...windPool),
        max: Math.max(...windPool),
        mean: windModelPool ? windModelPool.mean : Number.NaN,
      }
    : windModelPool;
  // Wave analogue of ensWidth: mean per-hour spread (max−min) across the wave
  // models. Multi-model disagreement between deterministic runs, NOT an
  // ensemble — the prose must never call it one. An hour with fewer than 2
  // reporting models has no spread to measure and is skipped.
  let waveSpreadSum = 0;
  let waveSpreadN = 0;
  for (let i = a; i <= b; i++) {
    const v: number[] = [];
    for (const arr of waveByModel) {
      const x = arr?.[i];
      if (x !== null && x !== undefined) v.push(x);
    }
    if (v.length >= 2) {
      waveSpreadSum += Math.max(...v) - Math.min(...v);
      waveSpreadN++;
    }
  }
  const waveSpread = waveSpreadN ? waveSpreadSum / waveSpreadN : null;
  const wave = poolStats(waveByModel, a, b);
  const per = stats(periodSeries, a, b);
  const swell = stats(swellH, a, b);
  const windSea = stats(windSeaH, a, b);
  const swP = stats(swellPer, a, b);
  const cur = stats(curVelKn, a, b);

  let sqMax = 0;
  for (let i = a; i <= b; i++) sqMax = Math.max(sqMax, fc.squall?.level[i] ?? 0);

  if (!wind && !wave) {
    return `<p class="b-note">No forecast data in this window.</p>`;
  }

  // Overall verdict from the worst of wind (Beaufort) and sea state, plus squalls.
  const bfMax = wind ? beaufort(wind.max) : null;
  const bfIdx = bfMax ? BEAUFORT.indexOf(bfMax) : 0;
  let vi = 0; // 0 comfortable → 3 dangerous
  if (wave) vi = Math.max(vi, wave.max > 4 ? 3 : wave.max > 2.5 ? 2 : wave.max > 1.5 ? 1 : 0);
  if (bfIdx >= 8) vi = 3;
  else if (bfIdx >= 7) vi = Math.max(vi, 2);
  else if (bfIdx >= 6) vi = Math.max(vi, 1);
  if (sqMax >= 3) vi = Math.max(vi, 2);
  else if (sqMax >= 2) vi = Math.max(vi, 1);

  // v1's --serious maps to --warn; keep --good / --warn / --crit / --muted.
  const VERDICTS: readonly [string, string][] = [
    ["Workable", "--good"],
    ["Moderate", "--warn"],
    ["Difficult", "--warn"],
    ["Hazardous", "--crit"],
  ];
  const V: [string, string] =
    scope?.lvl === 2 ? ["Out of scope", "--muted"] : VERDICTS[vi];

  const P: string[] = [];
  P.push(
    `<p class="lede">${start === 0 ? "Next" : ""} ${len} h` +
      ` — ${fmt(a)} to ${fmt(b)}` +
      `<span class="verdict" style="background:var(${V[1]})">${V[0]}</span></p>`,
  );
  if (scope)
    P.push(
      scope.lvl === 2
        ? `<p style="border-left:3px solid var(--crit);padding-left:10px">⚠ ${scope.msg}</p>`
        : `<p class="b-note">${scope.msg}</p>`,
    );

  // Wind sentence.
  if (wind && bfMax) {
    const bfMin = beaufort(wind.min);
    const term = bfMin[1] === bfMax[1] ? bfMax[1] : `${bfMin[1]} building to ${bfMax[1]}`;
    P.push(
      `<p><strong>Wind</strong> ${rng(wind, "kn")}` +
        ` (${term}) — ${bfMax[2]}.` +
        (ensWidth != null
          ? ` Ensemble uncertainty averages ±${(ensWidth / 2).toFixed(0)} kn` +
            ` (${
              ensWidth < ENSEMBLE_WIDTH_KN.tight
                ? "tight — a confident forecast"
                : ensWidth < ENSEMBLE_WIDTH_KN.wide
                  ? "moderate"
                  : "wide — the atmosphere hasn't settled"
            }).`
          : "") +
        `</p>`,
    );
  }

  // Wave + period + vessel-feel sentence.
  if (wave) {
    let s = `<p><strong>Seas</strong> ${rngLen(wave)} (${seaTerm(wave.max)})`;
    // Steepness, not period, decides how a sea feels — and it needs the height,
    // the period AND the depth, all of which are to hand.
    const lambda = per?.mean != null ? wavelengthM(per.mean, fc.depthM) : null;
    const steep = lambda != null && lambda > 0 && wave.mean != null ? wave.mean / lambda : null;
    if (per) {
      s += ` at ${rng(per, "s", 0)}`;
      if (lambda != null && steep != null) {
        s +=
          ` — wavelength about ${Math.round(convLen(lambda))} ${lenUnit()}, steepness ${steepnessAsRatio(steep)}` +
          ` — ${steepnessFeel(steep)}`;
      }
    }
    s += ".";
    // Mirrors the wind clause's ±half-width and its tight/moderate/wide read,
    // scaled to wave heights: ±0.25 m and ±0.75 m stand in for its ±4/±7.5 kn.
    if (waveSpread != null) {
      const half = waveSpread / 2;
      s +=
        ` Model spread averages ±${convLen(half).toFixed(1)} ${lenUnit()}` +
        ` (${
          half <= 0.25
            ? "tight — the wave models agree"
            : half < 0.75
              ? "moderate"
              : "wide — the wave models disagree"
        }).`;
    }
    // Name the period's provenance, and say why there is no matching spread
    // figure for it. A reader who sees "Model spread averages ±0.3 m" for
    // height and nothing for period is owed the reason: it is not that the
    // models agree on period, it is that their periods are not the same
    // quantity to spread.
    if (per && periodModel) {
      s +=
        ` Period is ${periodModel.label} — the wave models do not all publish` +
        ` the same period quantity, so it is read from one model rather than averaged.`;
    }
    // How THIS hull meets it: pitch from wavelength against her length, roll from
    // period against her natural roll period, and slamming only when the sea is
    // steep AND tall enough against her draft for the bow to come out.
    if (vessel && per?.mean != null) {
      const loa = vessel.loa_m;
      const draft = vessel.draft_m;
      const vesselName = vlabel || `a ${loa != null ? fmtLen(loa) : "? m"} vessel`;
      const notes: FeelNote[] = [];

      // ROLL — resonance when the period approaches her natural roll period. The
      // sharpest single thing that can be said about how a hull will behave, so it
      // scores highest when it lands and drops away quickly when it does not.
      if (rollT != null) {
        const ratio = per.mean / rollT;
        const res = rollResonance(per.mean, rollT);
        if (res === "hard") {
          notes.push({ score: 1, text: "periods right on her roll period — she will roll hard beam-on" });
        } else if (res === "lively") {
          notes.push({ score: 0.8, text: "periods near her roll period — lively rolling in beam seas" });
        } else if (ratio < 1) {
          notes.push({ score: 0.25, text: "periods well under her roll period — quick, less resonant motion" });
        } else {
          notes.push({ score: 0.2, text: "periods above her roll period — a slow, easy roll" });
        }
      }

      // PITCH — worst when the wavelength is near her length.
      if (lambda != null && loa != null && loa > 0) {
        const r = lambda / loa;
        notes.push({ score: r >= 0.8 && r <= 1.3 ? 0.9 : r < 0.6 || r > 2 ? 0.15 : 0.35, text: pitchFeel(r) });
      }

      // SLAMMING — bow emergence needs a steep sea AND height against her draft. A
      // 0.7 m sea cannot slam a hull drawing 3 m, however short the period.
      const slam = slamRisk(wave.mean ?? null, per.mean, fc.depthM, draft);
      if (slam === "hard") {
        notes.push({ score: 1, text: "steep, and deep enough against her draft to slam" });
      } else if (slam === "warn") {
        notes.push({ score: 0.85, text: "steep enough to risk slamming forward" });
      }

      // CROSS SEA — swell and wind sea running at an angle. No single number shows
      // this, and it is the condition crews describe as the sea having no rhythm.
      const swDir = meanBearing(fc.partition?.swellDir, a, b);
      const wsDir = meanBearing(fc.partition?.windSeaDir, a, b);
      if (swDir != null && wsDir != null && swell && windSea) {
        // Both trains have to be real for the sea to feel crossed: a 60° floor
        // rather than 40°, and the smaller train must be at least a third of the
        // larger. At 40° with a 0.75 m sea this called the Gulf "a confused, lumpy
        // sea with no rhythm", which is a phrase for weather, not for a flat
        // morning. Graded, too — 60° is uneven, 90°+ is genuinely confused. The
        // test itself is crossSea() above, shared with the conditions table.
        const sep = crossSea(swDir, wsDir, swell.mean, windSea.mean);
        if (sep !== null) {
          const lesser = Math.min(swell.mean, windSea.mean);
          const greater = Math.max(swell.mean, windSea.mean);
          notes.push({
            score: 0.55 + Math.min(0.35, (sep / 180) * 0.4 + (lesser / greater) * 0.15),
            text:
              `swell and wind sea about ${Math.round(sep)}° apart — ` +
              (sep >= CROSS_SEA.confusedDeg
                ? "a confused, lumpy sea with no rhythm"
                : "an uneven, cross-running sea"),
          });
        }
      }

      // WIND AGAINST SWELL — opposed wind steepens crests beyond what the height
      // and period alone imply, and it is why a forecast can under-read.
      const wDir = meanBearing(fc.wind?.directionDeg, a, b);
      if (windAgainstSwell(wDir, swDir, wind?.mean ?? null)) {
        notes.push({
          score: 0.7,
          text: "wind blowing against the swell — crests steepen and break earlier than the numbers suggest",
        });
      }

      if (notes.length > 0) {
        // Top two only. More than that and it becomes a list nobody finishes.
        const top = notes.sort((x, y) => y.score - x.score).slice(0, 2);
        const particulars = [loa != null ? fmtLen(loa) : null, rollT != null ? `≈${rollT}s natural roll` : null]
          .filter((x) => x !== null)
          .join(", ");
        s +=
          ` For <strong>${vesselName}</strong>${particulars ? ` (${particulars})` : ""}: ` +
          top.map((n) => n.text).join("; ") +
          (vessel.dp && top.some((n) => n.score >= 0.8)
            ? "; DP lets her hold a heading that softens it"
            : "") +
          ".";
      }
    }
    s += "</p>";
    P.push(s);
  }

  // Swell vs wind sea.
  if (swell && windSea && (swell.mean > 0.3 || windSea.mean > 0.3)) {
    const dom = windSea.mean > swell.mean ? "wind sea" : "swell";
    const isLongSwell = longSwell(swP?.mean ?? null);
    P.push(
      `<p><strong>Sea character</strong> — ${dom}-dominated` +
        (swP?.mean != null ? `; swell running ${swP.mean.toFixed(0)}s` : "") +
        `. ${
          dom === "wind sea"
            ? "Locally driven, choppier and wetter on deck."
            : isLongSwell
              ? "Distant-swell energy — a long, predictable, rolling motion."
              : "Short-period swell — still a choppy, close-interval sea."
        }</p>`,
    );
  }

  // Squalls.
  if (sqMax >= 1) {
    P.push(
      `<p><strong>Squalls</strong> — ${
        sqMax >= 3
          ? "severe convective signal"
          : sqMax >= 2
            ? "squalls likely at times"
            : "a slight chance of squalls"
      }` +
        ` in this window; brief gusts can far exceed the mean, and lightning would halt` +
        ` crane, transfer and deck work.</p>`,
    );
  }

  // Daylight & darkness within the window.
  if (solar?.level) {
    let downH = 0;
    let darkH = 0;
    for (let i = a; i <= b; i++) {
      const L = solar.level[i] ?? 0;
      if (L >= 1) downH++;
      if (L >= 2) darkH++;
    }
    const dayH = b - a + 1 - downH;
    const dayOnly = vessel?.day_only ?? false;
    if (downH > 0 || dayOnly) {
      let s =
        `<p><strong>Daylight</strong> — this ${len} h window holds about ` +
        `${dayH} h of daylight and ${downH} h with the sun down` +
        (darkH ? ` (${darkH} h fully dark)` : "") +
        `. `;
      s += dayOnly
        ? `<strong>${vlabel || "This vessel"} works in daylight only</strong>, so only the ` +
          `~${dayH} daylight hours are workable — plan the run inside them.`
        : `Night deck work (cranes, CPT pushes, transfers) depends on the client's ` +
          `HSE rules and deck lighting; some operations pause after dark.`;
      s += `</p>`;
      P.push(s);
    }
  }

  // Seabed depth & relief around the point (NCEI bathymetry).
  const relief = fc.relief;
  if (relief || depthM != null) {
    const pt = depthM != null ? Math.round(depthM) : null;
    const md = (m: number): string => Math.round(convLen(m)).toLocaleString();
    let s = `<p><strong>Seabed</strong> — `;
    if (pt != null) s += `about <strong>${md(pt)} ${lenUnit()}</strong> at the point`;
    if (relief) {
      const spread = relief.deep - relief.shallow;
      const term =
        spread < 15
          ? "flat seabed"
          : spread < 60
            ? "gentle relief"
            : spread < 200
              ? "a noticeable slope"
              : "steep relief (a slope or escarpment)";
      s += `${pt != null ? ", ranging" : "ranges"} ${md(relief.shallow)}–${md(relief.deep)} ${lenUnit()} within ~${fmtKm(5)} — ${term}. `;
      s +=
        spread < 60
          ? "Even ground for coring, CPT and anchoring."
          : "Worth checking before siting coring/CPT stations, anchoring or cable routes — the bottom is not level here.";
    } else s += ".";
    s += ` <span class="quiet">NCEI global bathymetry (coarse ~500 m grid).</span></p>`;
    P.push(s);
  }

  // Currents.
  if (cur && cur.max >= 0.5) {
    P.push(
      `<p><strong>Current</strong> ${rng(cur, "kn", 1)}` +
        (cur.max >= 1.5
          ? " — strong enough to affect station-keeping, streamer feathering and ROV work."
          : " — modest, but worth noting for drift.") +
        `</p>`,
    );
  }

  P.push(
    `<p class="b-note">Deterministic summary from the forecast numbers — no figure` +
      ` is invented. Vessel roll-period estimates and comfort thresholds are provisional` +
      ` (see methodology).</p>`,
  );
  return P.join("");
}
